import { useMemo } from 'react'

import { LoadingButton } from '@mui/lab'
import { Button } from '@mui/material'
import { GridCellParams, GridRowModel, GridRowParams } from '@mui/x-data-grid'
import clsx from 'clsx'
import { format, parseJSON } from 'date-fns'

import { IGridColumn } from 'components/common'
import { HubSpotSynchronizationTypeMap } from 'constants/index'

interface IHubSpotSynchronizationLogsColumnsProps {
  handleViewClick: (params: GridRowParams) => void
  handleRetryClick: (params: GridRowParams) => void
  loadingMap: Record<string, boolean>
}

const useHubSpotSynchronizationLogsColumns = (_props: IHubSpotSynchronizationLogsColumnsProps) => {
  const { handleViewClick, handleRetryClick, loadingMap } = _props

  const columns: IGridColumn[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID' },
      {
        field: 'createdAt',
        headerName: 'Timestamp of export attempt',
        minWidth: 250,
        valueFormatter: (params) =>
          params.value ? format(parseJSON(params.value), 'dd/MM/yyyy HH:mm:ss') : '',
      },
      { field: 'onepointEntityName', headerName: 'Entity Name in OP', minWidth: 150 },
      { field: 'onepointEntityId', headerName: 'OP Entity ID', minWidth: 150 },
      {
        field: 'hubSpotObjectName',
        headerName: 'Object Name in HS',
        minWidth: 150,
      },
      {
        field: 'hubSpotObjectId',
        headerName: 'HS Record ID',
        minWidth: 150,
        valueFormatter: (params) => params.value ?? 'N/A',
      },

      {
        field: 'message',
        headerName: 'Message',
      },
      {
        field: 'rawResponse',
        headerName: 'HS Response',
        valueFormatter: (params) => (!params.value || !params.value.trim() ? 'N/A' : params.value),
      },
      {
        field: 'type',
        headerName: 'Action Type',
        minWidth: 200,
        valueFormatter: (params) => {
          return HubSpotSynchronizationTypeMap[params.value as string] || params.value
        },
      },
      {
        field: 'isSuccessful',
        headerName: 'Status',
        valueFormatter: (params) => (params.value ? 'Successful' : 'Failed'),
        cellClassName: (params: GridCellParams<GridRowModel>) => {
          return clsx({
            successfulStatus: params.value,
            failedStatus: !params.value,
          })
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        minWidth: 200,
        getActions: (params: GridRowParams) => {
          return [
            <Button key={params.id} variant='outlined' onClick={() => handleViewClick(params)}>
              View
            </Button>,
            <LoadingButton
              loading={loadingMap[params.id]}
              disabled={loadingMap[params.id]}
              key={params.id}
              variant='outlined'
              onClick={() => handleRetryClick(params)}>
              Retry
            </LoadingButton>,
          ]
        },
      },
    ],
    [handleRetryClick, handleViewClick, loadingMap],
  )
  return columns
}

export default useHubSpotSynchronizationLogsColumns
